import React, { useState, useEffect } from "react";
import axiosInstance from "../../config/axios";
import { formatCurrency, getEFTTypeDisplayName } from "../../utils/eftUtils";
import { getTradeTaxRate } from "../../constants/tax";
import { roundTo2 } from "../../utils/numberUtils";

const TradePaymentDetailsView = ({ trade }) => {
  const [realEstateTrustEFTs, setRealEstateTrustEFTs] = useState([]);

  const [commissionTrustEFTs, setCommissionTrustEFTs] = useState([]);

  const [ledgerEntries, setLedgerEntries] = useState([]);

  const [generalAccountEFTs, setGeneralAccountEFTs] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  useEffect(() => {
    if (!trade || !trade._id) return;

    setLoading(true);

    setError("");

    // Fetch Real Estate Trust EFTs

    axiosInstance

      .get(`/real-estate-trust-eft/trade/${trade._id}`)

      .then((res) => setRealEstateTrustEFTs(res.data))

      .catch(() => setRealEstateTrustEFTs([]));

    // Fetch Commission Trust EFTs

    axiosInstance

      .get(`/commission-trust-eft/trade/${trade._id}`)

      .then((res) => setCommissionTrustEFTs(res.data))

      .catch(() => setCommissionTrustEFTs([]));

    // Fetch ledger entries and filter for this trade

    axiosInstance

      .get("/ledger")

      .then((res) => {
        const allLedgerEntries = res.data || [];

        const tradeNumber = trade.tradeNumber || trade.keyInfo?.tradeNumber;

        const filteredEntries = allLedgerEntries.filter(
          (entry) =>
            entry.tradeNumber === tradeNumber ||
            (entry.description &&
              entry.description.includes(`Trade #: ${tradeNumber}`)),
        );

        setLedgerEntries(filteredEntries);
      })

      .catch(() => setLedgerEntries([]));

    // For General Account EFTs, we need a vendorId (not directly available from trade)

    // If you have a way to relate trade to vendor, add logic here. For now, leave empty.

    setLoading(false);
  }, [trade]);

  return (
    <div className="mb-8">
      {/* Real Estate Trust Payments Table */}

      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">
          Real Estate Trust Payments
        </h3>

        {realEstateTrustEFTs.length > 0 ? (
          <table className="min-w-full border border-gray-300 mb-2 text-sm">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left bg-gray-100 border">
                  EFT #
                </th>

                <th className="px-2 py-1 text-left bg-gray-100 border">Date</th>

                <th className="px-2 py-1 text-left bg-gray-100 border">
                  Amount
                </th>

                <th className="px-2 py-1 text-left bg-gray-100 border">
                  Recipient
                </th>

                <th className="px-2 py-1 text-left bg-gray-100 border">Type</th>

                <th className="px-2 py-1 text-left bg-gray-100 border">
                  Description
                </th>
              </tr>
            </thead>

            <tbody>
              {realEstateTrustEFTs.map((eft) => (
                <tr key={eft._id}>
                  <td className="border px-2 py-1">{eft.eftNumber}</td>

                  <td className="border px-2 py-1">
                    {eft.chequeDate || eft.date
                      ? new Date(
                          eft.chequeDate || eft.date,
                        ).toLocaleDateString()
                      : "-"}
                  </td>

                  <td className="border px-2 py-1">
                    {formatCurrency(eft.amount)}
                  </td>

                  <td className="border px-2 py-1">{eft.recipient}</td>

                  <td className="border px-2 py-1">
                    {getEFTTypeDisplayName(eft.type)}
                  </td>

                  <td className="border px-2 py-1">{eft.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-gray-500">
            No Real Estate Trust Payments found for this trade.
          </div>
        )}
      </div>

      {/* Commission Trust Activity Table */}

      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">
          Commission Trust Activity
        </h3>

        {(() => {
          // For Listing Side and Agent Double Ender, include CommissionTransfer from Real Estate Trust Payments as first transaction

          // For Co-Operating Side, include CommissionTransfer with "Commission Received" description as first transaction

          const tradeClassification = trade?.keyInfo?.classification || "";

          let displayCommissionTrustEFTs = [...commissionTrustEFTs];

          // Normalize classification for comparison (case-insensitive)

          const normalizedClassification = (tradeClassification || "")

            .toUpperCase()

            .trim();

          if (
            normalizedClassification === "LISTING SIDE" ||
            normalizedClassification === "AGENT DOUBLE ENDER" ||
            normalizedClassification === "AGENT DOUBLLE ENDER"
          ) {
            const commissionTransferEFT = realEstateTrustEFTs.find(
              (eft) => eft.type === "CommissionTransfer",
            );

            if (commissionTransferEFT) {
              displayCommissionTrustEFTs = [
                commissionTransferEFT,

                ...commissionTrustEFTs,
              ];
            }

            // Check if trust amount is less than total commission and add "Received From" transaction as second item

            const trustRecords = trade.trustRecords || [];

            const commissionIncome =
              trade.commission?.commissionIncomeRows?.[0] || {};

            const listingAmount = commissionIncome.listingAmount
              ? parseFloat(commissionIncome.listingAmount)
              : 0;

            const sellingAmount = commissionIncome.sellingAmount
              ? parseFloat(commissionIncome.sellingAmount)
              : 0;

            const taxRateForTrust = getTradeTaxRate(trade);

            const listingTaxTrust = roundTo2(listingAmount * taxRateForTrust);

            const sellingTaxTrust = roundTo2(sellingAmount * taxRateForTrust);

            const totalCommission = roundTo2(
              listingAmount + sellingAmount + listingTaxTrust + sellingTaxTrust,
            );

            const trustAmount = trustRecords.reduce((sum, record) => {
              const amount = parseFloat(
                record.amount || record.depositAmount || record.total || 0,
              );

              return sum + amount;
            }, 0);

            if (trustAmount < totalCommission) {
              let receivedFromLedger = null;

              if (ledgerEntries && ledgerEntries.length > 0) {
                const commissionTransferEFTNumber =
                  commissionTransferEFT?.eftNumber?.toString() || "";

                const candidateEntries = ledgerEntries.filter(
                  (entry) =>
                    entry.accountNumber === "10004" &&
                    entry.debit > 0 &&
                    entry.description &&
                    (entry.description.includes("Received From:") ||
                      entry.description.includes("Received from:")) &&
                    // Exclude if it matches the CommissionTransfer EFT number

                    entry.eftNumber?.toString() !==
                      commissionTransferEFTNumber &&
                    // Exclude entries that have "Commission Trust" in description (those are from CommissionTransfer)

                    !entry.description.includes("Commission Trust") &&
                    // Exclude entries that have "Commission Trust" anywhere (to be safe)

                    !entry.description

                      .toLowerCase()

                      .includes("commission trust"),
                );

                // If we have candidates, prefer the one that matches the pattern from final transactions

                if (candidateEntries.length > 0) {
                  // Find the one that matches the final transaction pattern (has " - Received From:" with dash before it)

                  receivedFromLedger =
                    candidateEntries.find((entry) =>
                      entry.description.includes(" - Received From:"),
                    ) || candidateEntries[0];
                }
              }

              if (receivedFromLedger) {
                // Extract recipient from description

                let recipient = receivedFromLedger.payee || "";

                if (!recipient && receivedFromLedger.description) {
                  const receivedFromMatch =
                    receivedFromLedger.description.match(
                      /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                    );

                  if (receivedFromMatch) {
                    recipient = receivedFromMatch[1].trim();
                  }
                }

                // Create the balance commission payment transaction from ledger entry

                const balanceCommissionPayment = {
                  eftNumber:
                    receivedFromLedger.eftNumber ||
                    receivedFromLedger.reference?.replace("EFT#", "") ||
                    "",

                  date:
                    receivedFromLedger.date || receivedFromLedger.chequeDate,

                  chequeDate:
                    receivedFromLedger.chequeDate || receivedFromLedger.date,

                  amount: receivedFromLedger.debit || receivedFromLedger.amount,

                  recipient: recipient || "N/A",

                  type: "BalanceCommissionPayment",

                  description: "Balance Amount Received",

                  _id: `balance-${receivedFromLedger._id || Date.now()}`,
                };

                // Insert as second transaction (after CommissionTransfer)

                if (commissionTransferEFT) {
                  displayCommissionTrustEFTs = [
                    commissionTransferEFT,

                    balanceCommissionPayment,

                    ...commissionTrustEFTs,
                  ];
                } else {
                  displayCommissionTrustEFTs = [
                    balanceCommissionPayment,

                    ...commissionTrustEFTs,
                  ];
                }
              } else {
                // Fallback: Find the "Received From" transaction from trust records

                const receivedFromRecord = trustRecords.find(
                  (record) =>
                    record.receivedFrom && record.paymentType === "EFT",
                );

                if (receivedFromRecord) {
                  // Extract EFT number from reference or use a default

                  const eftNumber =
                    receivedFromRecord.reference?.replace("EFT#", "") ||
                    receivedFromRecord.eftNumber ||
                    "";

                  // Create the balance commission payment transaction

                  const balanceCommissionPayment = {
                    eftNumber: eftNumber,

                    date:
                      receivedFromRecord.depositDate || receivedFromRecord.date,

                    chequeDate:
                      receivedFromRecord.depositDate || receivedFromRecord.date,

                    amount: parseFloat(
                      receivedFromRecord.amount ||
                        receivedFromRecord.depositAmount ||
                        receivedFromRecord.total ||
                        0,
                    ),

                    recipient: receivedFromRecord.receivedFrom || "",

                    type: "BalanceCommissionPayment",

                    description: "Balance Amount Received",

                    _id: `balance-${receivedFromRecord._id || Date.now()}`,
                  };

                  // Insert as second transaction (after CommissionTransfer)

                  if (commissionTransferEFT) {
                    displayCommissionTrustEFTs = [
                      commissionTransferEFT,

                      balanceCommissionPayment,

                      ...commissionTrustEFTs,
                    ];
                  } else {
                    displayCommissionTrustEFTs = [
                      balanceCommissionPayment,

                      ...commissionTrustEFTs,
                    ];
                  }
                }
              }
            }
          } else if (normalizedClassification === "CO-OPERATING SIDE") {
            // First, try to find CommissionTransfer in Real Estate Trust Payments

            let commissionReceivedEFT = realEstateTrustEFTs.find(
              (eft) => eft.type === "CommissionTransfer",
            );

            // If not found, look for a transaction with "Received From" in Commission Trust EFTs

            if (!commissionReceivedEFT) {
              commissionReceivedEFT = commissionTrustEFTs.find(
                (eft) =>
                  eft.description &&
                  (eft.description.includes("Received From") ||
                    eft.description.includes("Received from") ||
                    eft.description.match(
                      /Received From:.*Commission Trust/i,
                    ) ||
                    eft.description.includes("Commission Received")),
              );
            }

            // If still not found, check ledger entries from trade finalize (account 10004 with "Received From")

            if (
              !commissionReceivedEFT &&
              ledgerEntries &&
              ledgerEntries.length > 0
            ) {
              const commissionReceivedLedger = ledgerEntries.find(
                (entry) =>
                  entry.accountNumber === "10004" &&
                  entry.debit > 0 &&
                  entry.description &&
                  (entry.description.includes("Received From") ||
                    entry.description.includes("Received from")),
              );

              if (commissionReceivedLedger) {
                // Extract recipient from description

                let recipient = commissionReceivedLedger.payee || "";

                if (!recipient && commissionReceivedLedger.description) {
                  const receivedFromMatch =
                    commissionReceivedLedger.description.match(
                      /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                    );

                  if (receivedFromMatch) {
                    recipient = receivedFromMatch[1].trim();
                  }
                }

                // Create EFT-like object from ledger entry

                commissionReceivedEFT = {
                  eftNumber:
                    commissionReceivedLedger.eftNumber ||
                    commissionReceivedLedger.reference?.replace("EFT#", "") ||
                    "",

                  date:
                    commissionReceivedLedger.date ||
                    commissionReceivedLedger.chequeDate,

                  chequeDate:
                    commissionReceivedLedger.chequeDate ||
                    commissionReceivedLedger.date,

                  amount:
                    commissionReceivedLedger.debit ||
                    commissionReceivedLedger.amount,

                  recipient: recipient || "N/A",

                  type: "CommissionTransfer",

                  description: "Commission Received",

                  _id: commissionReceivedLedger._id,
                };
              }
            }

            // If still not found, check by recipient name (common outside broker names)

            if (!commissionReceivedEFT) {
              commissionReceivedEFT = commissionTrustEFTs.find(
                (eft) =>
                  eft.recipient &&
                  (eft.recipient.includes("HomeLife") ||
                    eft.recipient.includes("Future Realty") ||
                    eft.recipient.includes("Realty Inc")),
              );
            }

            if (commissionReceivedEFT) {
              // Create a formatted transaction with "Commission Received" as description

              const formattedEFT = {
                ...commissionReceivedEFT,

                description: "Commission Received",
              };

              // If the transaction was found in commissionTrustEFTs, remove it from the array first

              const remainingEFTs =
                commissionReceivedEFT._id &&
                commissionTrustEFTs.some(
                  (eft) => eft._id === commissionReceivedEFT._id,
                )
                  ? commissionTrustEFTs.filter(
                      (eft) => eft._id !== commissionReceivedEFT._id,
                    )
                  : commissionTrustEFTs;

              displayCommissionTrustEFTs = [formattedEFT, ...remainingEFTs];
            }
          }

          return displayCommissionTrustEFTs.length > 0 ? (
            <table className="min-w-full border border-gray-300 mb-2 text-sm">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left bg-gray-100 border">
                    EFT #
                  </th>

                  <th className="px-2 py-1 text-left bg-gray-100 border">
                    Date
                  </th>

                  <th className="px-2 py-1 text-left bg-gray-100 border">
                    Amount
                  </th>

                  <th className="px-2 py-1 text-left bg-gray-100 border">
                    Recipient
                  </th>

                  <th className="px-2 py-1 text-left bg-gray-100 border">
                    Type
                  </th>

                  <th className="px-2 py-1 text-left bg-gray-100 border">
                    Description
                  </th>
                </tr>
              </thead>

              <tbody>
                {displayCommissionTrustEFTs.map((eft) => (
                  <tr key={eft._id || eft.eftNumber}>
                    <td className="border px-2 py-1">{eft.eftNumber}</td>

                    <td className="border px-2 py-1">
                      {eft.chequeDate || eft.date
                        ? new Date(
                            eft.chequeDate || eft.date,
                          ).toLocaleDateString()
                        : "-"}
                    </td>

                    <td className="border px-2 py-1">
                      {formatCurrency(eft.amount)}
                    </td>

                    <td className="border px-2 py-1">{eft.recipient}</td>

                    <td className="border px-2 py-1">
                      {getEFTTypeDisplayName(eft.type)}
                    </td>

                    <td className="border px-2 py-1">{eft.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-gray-500">
              No Commission Trust Activity found for this trade.
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default TradePaymentDetailsView;
